import movement from 'GeneralUI/Timer/movement'
import {
  login,
  adminLogin,
  logout as apiLogout,
  forgotPassword,
  passwordReset as apiPasswordReset,
  lockUserAccount as emailLockUserAccount,
  verifyEmail as apiVerifyEmail,
  verifyPassword as apiVerifyPassword,
  resendVerificationEmail as apiResendVerificationEmail
} from 'Helper/Auth/summit-auth-api'
import { loadGlobalAppConfigAuth, loadLocalAppConfig } from 'actions/appConfigActions.js'

import debug from 'Helper/debugHelpers'
import {
  put,
  select,
  cancel,
  call,
  race,
  all
} from 'redux-saga/effects'
import { delay } from 'Helper/customReduxEffects.js'
import { get } from 'Helper/xtract_api'
import { updateLog } from 'actions/logActions'
import checkResult from './checkResult'
import { closeStream, xtractAPI } from 'actions/apiActions.js'

export function * handleSuccessfulLogin(action) {
  yield all([
    put(loadGlobalAppConfigAuth()),
    put(loadLocalAppConfig()),
    put(xtractAPI(`me/notification/metadata`, 'stream'))
  ])
}

export function * verifyPassword(action) {
  const result = yield call(apiVerifyPassword, action.password)
  yield call(checkResult, result, action)
}

export function * resendVerificationEmail(action) {
  const result = yield call(apiResendVerificationEmail, action.userId)
  yield call(checkResult, result, action)
}

export function * passwordReset(action) {
  const { password, passwordConfirmation } = action
  const { match = {} } = yield select(state => state.router)
  const { query = {} } = match
  debug.log('Submitting password reset call', debug.logLevels.AUTH)
  const response = yield call(
    apiPasswordReset,
    query.token,
    query.email,
    query.reset_expires ? query.reset_expires : query.expires,
    query.reset_signature ? query.reset_signature : query.signature,
    password,
    passwordConfirmation
  )
  yield call(checkResult, response, action)
}

export function * requestPasswordReset(action) {
  const { email } = action
  debug.log('Submitting request to verify email.', debug.logLevels.AUTH)
  const result = yield call(forgotPassword, email)
  yield call(checkResult, result, action)
}

export function * doLogin(action) {
  const { displayname, password } = action
  const { match = {} } = yield select(state => state.router)
  const { params = {} } = match
  let result
  if (params.subProvider && params.subProvider === 'xtract') {
    result = yield call(adminLogin, displayname, password)
  } else {
    result = yield call(login, displayname, password)
  }

  result = yield call(checkResult, result, action)

  if (result == null) {
    // there was an error. It was handled in checkResult so just return

  } else {
    // results checked out so reset failcount to 0
    yield put({
      type: 'SET_API_FAIL_COUNT',
      value: 0
    })
  }
}

export function * verifyEmail(action) {
  const { match = {} } = yield select(state => state.router)
  const { query = {} } = match
  debug.log('Submitting request to verify email.', debug.logLevels.AUTH)
  if (query.id && query.hash && query.signature, query.expires) {
    const result = yield call(apiVerifyEmail, query.id, query.hash, query.expires, query.signature)
    yield call(checkResult, result, action)
  }
}

export function * lockUserAccount(action) {
  const { match = {} } = yield select(state => state.router)
  const { query = {} } = match

  if (query.signature) {
    const result = yield call(emailLockUserAccount, query.lock_id, query.signature)
    // we have to manually dispatch this so the old style reducer will preform updates
    yield put({
      type: 'STORE_ACCESS_DELETE',
      result: result.data.data.access,
      server_time: result.server_time
    })
    yield call(checkResult, result, action)
  } else {
    // in this case the logged in user clicked the lock button so this can be handled by the normal
    // api functionality
    yield put({
      type: 'XTRACT_API_CALL',
      resource: 'access',
      method: 'delete',
      obj: null,
      id: action.user_id,
      callbacks: action.callbacks
    })
  }
}

export function * unlockUserAccount(action) {
  yield put({
    type: 'XTRACT_API_CALL',
    resource: 'access',
    method: 'create',
    obj: null,
    id: action.user_id,
    callbacks: action.callbacks
  })
}

// The longest the logout waits for the click log to send before moving on.
export const CLICK_LOG_FLUSH_TIMEOUT_MS = 3000

// Sends the click log events still waiting to go out while the token is valid.
// Called directly rather than through xtractAPI so that a failure stays quiet
// and cannot trigger the retry and error handling of a normal api call during
// logout. On success the sent events are removed from the log. On failure they
// stay, and XtractLogger sends them after the next login.
export function * flushClickLog() {
  const token = yield select(state => state.auth.token)
  const holding = yield select(state => state.log.holding)
  if (token == null || !Array.isArray(holding) || holding.length === 0) {
    return
  }
  const sentCount = holding.length
  try {
    const result = yield call(get, token, 'click_log', 'CREATE', null, holding)
    if (result?.status >= 200 && result?.status < 300) {
      // events recorded while the request was in flight are not part of this send
      const currentHolding = yield select(state => state.log.holding)
      yield put(updateLog(currentHolding.slice(sentCount)))
    } else {
      debug.log(`Click log send before logout failed with status ${result?.status}`, debug.logLevels.AUTH, 'warn')
    }
  } catch (error) {
    debug.log(`Click log send before logout failed: ${error}`, debug.logLevels.AUTH, 'warn')
  }
}

export function * logout(action) {
  let authResult
  movement.stop()
  yield put(closeStream('ME/NOTIFICATION/METADATA'))
  // most logout lives in the reducer, all we do here is cancel the
  // token refresh and send a request to the api to revoke the token
  const auth = yield select(state => state.auth)
  if (auth.refreshTimer != null) {
    yield cancel(auth.refreshTimer)
  }
  yield race({
    flush: call(flushClickLog),
    timeout: delay(CLICK_LOG_FLUSH_TIMEOUT_MS)
  })
  debug.log('Submitting logout api call', debug.logLevels.AUTH)
  try {
    authResult = yield call(apiLogout)
  } finally {
    yield put({ type: 'xtract-auth@reducer-logout', csrf_token: authResult?.data?.data?.logout?.csrf_token })
  }
}
